<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable">
      <report-print-names></report-print-names>
    </report-tools>

    <report-state v-if="!selectedCompanies.length" icon="mdi-domain" :title="$t('no_companies_selected')"></report-state>
    <report-state v-else-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-food-off"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <ui-table
      v-else
      variant="report"
      :columns="columns"
      :rows="rows"
      :sort="sort"
      :totals="totals"
      :total-label="$t('total')"
      @sort="sortBy"
    >
      <template #head-shift="{ column }">
        <report-shift-tag :shift="column.shift.id"></report-shift-tag>
      </template>

      <template #cell-quantity="{ row }"><b>{{ row.quantity }}</b></template>

      <template #cell-share="{ row }">
        <span class="rd-share">
          <span class="rd-mini"><i :style="{ width: `${row.relative}%` }"></i></span>
          <span class="rd-muted rd-num">{{ row.share }}%</span>
        </span>
      </template>
    </ui-table>
  </div>
</template>

<script>
import ReportTabGroupMealsTabMeals from "@/components/report/group-meals/ReportTabGroupMealsTabMeals.vue";
import { SHIFTS } from "@/constants/shifts";
import hasShiftBinding from "../../../shared/hasShiftBinding";
import hasShiftSubtitle from "../../../shared/hasShiftSubtitle";
import ReportShiftTag from "../../../shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "../../copyTable";
import hasTableSort from "../../hasTableSort";
import ReportPrintNames from "../../ReportPrintNames.vue";
import ReportState from "../../ReportState.vue";
import ReportTools from "../../ReportTools.vue";

/**
 * Jela iz grupa na računaru: jelo × smena sa udjelom u ukupnom. Firme (grupe), smene, period i pretragu čita iz
 * zajedničke trake; "nazivi firmi u štampi" je u alatima. Redosled je kao u odgovoru (po grupi jela), dok se ne
 * klikne zaglavlje. Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "ReportDesktopGroupMeals",
  extends: ReportTabGroupMealsTabMeals,
  mixins: [hasShiftBinding, hasShiftSubtitle, hasTableSort],
  components: { ReportPrintNames, ReportShiftTag, ReportState, ReportTools },
  data: () => ({
    sort: { key: "num", dir: 1 },
  }),
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "#", label: "#", index: true },
        { key: "name", label: this.$t("meal"), name: true, title: (row) => `${row.name} · ID ${row.id}`, sortable: true },
        ...this.visibleShifts.map((shift) => ({ key: shift.key, slot: "shift", shift, align: "end", num: true, zero: true, sortable: true })),
        { key: "quantity", label: this.$t("total"), align: "end", num: true, sortable: true },
        { key: "share", label: this.$t("report_desktop.share"), cls: "rd-col-share" },
      ];
    },
    /** Smene 1–3 uvek (ako su izabrane), četvrta samo kad ima obroka. */
    visibleShifts() {
      return SHIFTS.filter(
        (shift) =>
          this.selectedShifts.includes(shift.id) && (shift.id < 4 || this.foods.some((meal) => meal[shift.key] > 0))
      );
    },
    rows() {
      const found = this.filters.search
        ? this.customSearch(
            this.foods.map((meal) => ({ ...meal, name: this.toLatin(meal.meal_name) })),
            [{ value: "name" }],
            this.filters.search
          )
        : this.foods.map((meal) => ({ ...meal, name: this.toLatin(meal.meal_name) }));
      const visible = found.filter((meal) => meal.quantity > 0);
      const grand = visible.reduce((sum, meal) => sum + meal.quantity, 0);
      const max = Math.max(1, ...visible.map((meal) => meal.quantity));
      return this.sortRows(
        visible.map((meal) => ({
          ...meal,
          share: grand ? Math.round((meal.quantity / grand) * 100) : 0,
          relative: Math.round((meal.quantity / max) * 100),
        }))
      );
    },
    totals() {
      const totals = { quantity: this.rows.reduce((sum, row) => sum + row.quantity, 0) };
      SHIFTS.forEach((shift) => {
        totals[shift.key] = this.rows.reduce((sum, row) => sum + (row[shift.key] || 0), 0);
      });
      return totals;
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("report_mobile.no_meals");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "total", label: this.$t("report_desktop.total_dishes"), value: this.totals.quantity, tone: "inverse" },
        { key: "meals", label: this.$t("meals_main"), value: this.rows.length },
        { key: "companies", label: this.$t("companies"), value: this.selectedCompanies.length },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    async copyTable() {
      const headers = [this.$t("meal"), ...this.visibleShifts.map((shift) => this.$t(shift.label)), this.$t("total")];
      const rows = this.rows.map((row) => [
        row.name,
        ...this.visibleShifts.map((shift) => row[shift.key]),
        row.quantity,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
